import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { AUTHENTICATED_AGREEMENT_DEFAULT_DOCUMENTS_VERSION } from "@src/appV2/WorkplaceAgreement/constants";
import { WorkplaceAgreementPerUserDialogView } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementPerUserDialogView";
import { isEmpty } from "lodash";
import { useState } from "react";

/**
 * "per-user" variation: prompts each workplace user (not once per workplace) until they personally
 * accept, gating on `GET .../agreements?scope=user` with no first-shift delay.
 */
export function WorkplaceAgreementPerUserDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const { documentsVersion } = useCbhFlag(CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT, {
    defaultValue: {
      variation: "off",
      flexAgreementUrl: "",
      hiringAgreementUrl: "",
    },
  });
  const agreementVersion =
    typeof documentsVersion === "number"
      ? documentsVersion
      : AUTHENTICATED_AGREEMENT_DEFAULT_DOCUMENTS_VERSION;

  const isWorkplaceUser = isDefined(workplaceId) && !isAdmin && !isEmpty(userId);

  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(
    workplaceId,
    {
      scope: "user",
      ...(typeof documentsVersion === "number" && { documentsVersion }),
    },
    { enabled: isWorkplaceUser }
  );

  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId ?? "");

  const [visible, setVisible] = useState<boolean>(true);

  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId: workplaceId ?? "",
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: agreementVersion,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!isWorkplaceUser) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  return (
    <WorkplaceAgreementPerUserDialogView
      isOpen={visible}
      isSubmitting={isLoadingCreateWorkplaceAgreement}
      onAgree={onAgree}
    />
  );
}
